import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import CharCounter from "../../components/CharCounter";
import autorizacoesService from "../../services/autorizacoes";
import { tipoAutorizacaoEnum } from "../../utils/constants";

const NOME_MAX = 100;
const DESC_MAX = 500;
const OBS_MAX = 500;

const emptyForm = () => ({
  id: null,
  nome: "",
  tipo: 0,
  isActive: true,
  itens: [{ descricao: "", obrigatorio: true, observacoes: "" }],
});

function AdicionarChecklistAutorizacao() {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const result = await autorizacoesService.listarChecklistsAutorizacaoPaginado({
        pageNumber: page + 1,
        pageSize,
      });
      setList(result.items);
      setTotal(result.totalCount);
    } catch (err) {
      setError(err?.message || "Erro ao carregar checklists.");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const abrirNovo = () => {
    setForm(emptyForm());
    setOpenForm(true);
  };

  const abrirEdicao = (item) => {
    setForm({
      id: item.id,
      nome: item.nome || "",
      tipo: item.tipo,
      isActive: item.isActive,
      itens:
        item.itens?.length > 0
          ? item.itens.map((i) => ({
              id: i.id,
              descricao: i.descricao || "",
              obrigatorio: !!i.obrigatorio,
              observacoes: i.observacoes || "",
            }))
          : [{ descricao: "", obrigatorio: true, observacoes: "" }],
    });
    setOpenForm(true);
  };

  const fechar = () => {
    setOpenForm(false);
    setError("");
  };

  const updateItem = (idx, field, value) => {
    setForm((f) => {
      const itens = [...f.itens];
      itens[idx] = { ...itens[idx], [field]: value };
      return { ...f, itens };
    });
  };

  const addItem = () => {
    setForm((f) => ({
      ...f,
      itens: [
        ...f.itens,
        { descricao: "", obrigatorio: true, observacoes: "" },
      ],
    }));
  };

  const removeItem = (idx) => {
    setForm((f) => {
      if (f.itens.length <= 1) return f;
      const itens = f.itens.filter((_, i) => i !== idx);
      return { ...f, itens };
    });
  };

  const validar = () => {
    if (!form.nome.trim() || form.nome.length > NOME_MAX) return "Informe um nome válido.";
    if (!form.itens?.length) return "Adicione pelo menos um item.";
    for (const it of form.itens) {
      if (!it.descricao.trim() || it.descricao.length > DESC_MAX)
        return "Descrição do item inválida.";
      if (it.observacoes && it.observacoes.length > OBS_MAX)
        return "Observações do item muito longas.";
    }
    return null;
  };

  const salvar = async () => {
    const err = validar();
    if (err) {
      setError(err);
      return;
    }
    try {
      setSaving(true);
      setError("");
      const payload = {
        id: form.id,
        nome: form.nome.trim(),
        tipo: Number(form.tipo),
        isActive: !!form.isActive,
        itens: form.itens.map((i) => ({
          id: i.id,
          descricao: i.descricao.trim(),
          obrigatorio: !!i.obrigatorio,
          observacoes: i.observacoes?.trim() || null,
        })),
      };
      if (form.id) {
        await autorizacoesService.atualizarChecklistAutorizacao(payload);
        setSuccess("Checklist atualizado com sucesso.");
      } else {
        await autorizacoesService.criarChecklistAutorizacao(payload);
        setSuccess("Checklist criado com sucesso.");
      }
      setOpenForm(false);
      carregar();
    } catch (e) {
      setError(e?.message || "Erro ao salvar checklist.");
    } finally {
      setSaving(false);
    }
  };

  const excluir = async (id) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm("Deseja realmente excluir este checklist?")) return;
    try {
      await autorizacoesService.deletarChecklistAutorizacao(id);
      setSuccess("Checklist excluído.");
      carregar();
    } catch (e) {
      setError(e?.message || "Erro ao excluir checklist.");
    }
  };

  const tipoLabel = (tipo) =>
    tipoAutorizacaoEnum.find((t) => t.value === tipo)?.label ?? "—";

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: { xs: 2, md: 4 },
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontSize: { xs: "1.35rem", sm: "1.75rem", md: "2.125rem" } }}
        >
          Checklist de Autorização
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={abrirNovo}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          Novo Checklist
        </Button>
      </Box>

      {error && !openForm && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Paper>
        {loading ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : list.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography color="text.secondary">
              Nenhum checklist cadastrado.
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Ativo</TableCell>
                  <TableCell>Itens</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((it) => (
                  <TableRow key={it.id} hover>
                    <TableCell>{it.nome}</TableCell>
                    <TableCell>{tipoLabel(it.tipo)}</TableCell>
                    <TableCell>{it.isActive ? "Sim" : "Não"}</TableCell>
                    <TableCell>{it.itens?.length ?? 0}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => abrirEdicao(it)}
                        aria-label={`Editar ${it.nome}`}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => excluir(it.id)}
                        aria-label={`Excluir ${it.nome}`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      <Dialog
        open={openForm}
        onClose={fechar}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { m: { xs: 1, sm: 2 } },
        }}
      >
        <DialogTitle>
          {form.id ? "Editar Checklist" : "Novo Checklist"}
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nome"
                fullWidth
                required
                value={form.nome}
                inputProps={{ maxLength: NOME_MAX }}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <CharCounter value={form.nome} maxLength={NOME_MAX} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel id="tipo-label">Tipo</InputLabel>
                <Select
                  labelId="tipo-label"
                  label="Tipo"
                  value={form.tipo}
                  onChange={(e) =>
                    setForm({ ...form, tipo: Number(e.target.value) })
                  }
                >
                  {tipoAutorizacaoEnum.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(e) =>
                      setForm({ ...form, isActive: e.target.checked })
                    }
                  />
                }
                label="Ativo"
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: { xs: "stretch", sm: "center" },
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1,
                  mt: 1,
                }}
              >
                <Typography variant="subtitle1">Itens</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addItem}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  Adicionar item
                </Button>
              </Box>
            </Grid>
            {form.itens.map((it, idx) => (
              <Grid item xs={12} key={it.id ?? `new-${idx}`}>
                <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Descrição"
                        fullWidth
                        required
                        value={it.descricao}
                        inputProps={{ maxLength: DESC_MAX }}
                        onChange={(e) =>
                          updateItem(idx, "descricao", e.target.value)
                        }
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <CharCounter
                                value={it.descricao}
                                maxLength={DESC_MAX}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Observações"
                        fullWidth
                        value={it.observacoes}
                        inputProps={{ maxLength: OBS_MAX }}
                        onChange={(e) =>
                          updateItem(idx, "observacoes", e.target.value)
                        }
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <CharCounter
                                value={it.observacoes}
                                maxLength={OBS_MAX}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!it.obrigatorio}
                            onChange={(e) =>
                              updateItem(idx, "obrigatorio", e.target.checked)
                            }
                          />
                        }
                        label="Obrig."
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={1}
                      sx={{ textAlign: { xs: "right", sm: "center" } }}
                    >
                      <IconButton
                        onClick={() => removeItem(idx)}
                        disabled={form.itens.length <= 1}
                        aria-label="Remover item"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 0 },
            px: { xs: 2, sm: 3 },
            pb: { xs: 2, sm: 2 },
            "& > button": { width: { xs: "100%", sm: "auto" } },
          }}
        >
          <Button onClick={fechar} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={salvar} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AdicionarChecklistAutorizacao;
